import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from './Button';
import { Icon } from './icons';

/** How long a copy stays confirmed (P15c, backlog §24: every action confirms itself). */
export const COPY_CONFIRM_MS = 1500;

/**
 * Copies `text` and says so: a check and `copiedLabel` for COPY_CONFIRM_MS (entering with os-enter), `failedLabel` when
 * the clipboard refuses, then its own label again. Screen readers hear the outcome once.
 */
export function CopyButton({
  text,
  copiedLabel,
  failedLabel,
  children,
  ...rest
}: Omit<ComponentProps<typeof Button>, 'onClick' | 'children'> & { text: string; copiedLabel: string; failedLabel: string; children: ReactNode }) {
  const [outcome, setOutcome] = useState<'copied' | 'failed' | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    let ok = true;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      ok = false;
    }
    setOutcome(ok ? 'copied' : 'failed');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOutcome(null), COPY_CONFIRM_MS);
  };

  const label = outcome === 'copied' ? copiedLabel : outcome === 'failed' ? failedLabel : null;
  return (
    <>
      <Button {...rest} onClick={() => void copy()}>
        {outcome === 'copied' ? (
          <span key="copied" data-copied className="os-enter inline-flex items-center gap-1.5">
            <Icon icon={Check} size={14} />
            {copiedLabel}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <Icon icon={Copy} size={14} />
            {label ?? children}
          </span>
        )}
      </Button>
      <span role="status" className="sr-only">
        {label ?? ''}
      </span>
    </>
  );
}
